/**
 * Double doors in the view: a straight run of door squares ('+' closed, "'" open) is one door,
 * drawn as one strip with posts only at its ends. No DOM here, so it is unit-tested directly.
 */
import type { DungeonView } from '../shared/view.ts';

/** Matches the engine's longest doorway (systems/doors.ts); longer runs are single doors. */
const MAX_DOORWAY = 4;

type Tiles = Pick<DungeonView, 'tiles' | 'width' | 'height'>;

export interface DoorRun {
  /** The door spans a passage running east to west (its leaves line up north to south). */
  vertical: boolean;
  /** This square's place in the run, from the west or north end. */
  index: number;
  length: number;
}

function tileAt(d: Tiles, x: number, y: number): string {
  return x < 0 || y < 0 || x >= d.width || y >= d.height ? ' ' : d.tiles[y * d.width + x];
}

export function isDoorChar(t: string): boolean {
  return t === '+' || t === "'";
}

/** The run of door squares through (x, y), or null if it isn't a door. */
export function doorRun(d: Tiles, x: number, y: number): DoorRun | null {
  if (!isDoorChar(tileAt(d, x, y))) return null;
  const door = (px: number, py: number) => isDoorChar(tileAt(d, px, py));
  const along = door(x - 1, y) || door(x + 1, y) ? { dx: 1, dy: 0 } : door(x, y - 1) || door(x, y + 1) ? { dx: 0, dy: 1 } : null;
  if (!along) {
    const solid = (py: number) => tileAt(d, x, py) === '#';
    return { vertical: solid(y - 1) && solid(y + 1), index: 0, length: 1 };
  }
  let before = 0, after = 0;
  while (before <= MAX_DOORWAY && door(x - along.dx * (before + 1), y - along.dy * (before + 1))) before++;
  while (after <= MAX_DOORWAY && door(x + along.dx * (after + 1), y + along.dy * (after + 1))) after++;
  const length = before + after + 1;
  const vertical = along.dy === 1;
  return length > MAX_DOORWAY ? { vertical, index: 0, length: 1 } : { vertical, index: before, length };
}

/** Tile indices of every square in the run that (x, y) belongs to. */
export function doorSquares(width: number, x: number, y: number, run: DoorRun): number[] {
  const out: number[] = [];
  for (let k = 0; k < run.length; k++) {
    const px = run.vertical ? x : x - run.index + k, py = run.vertical ? y - run.index + k : y;
    out.push(py * width + px);
  }
  return out;
}

/**
 * Is (x, y) a closed door the party knows is trapped: a live trap's mark covers one of its leaves? Walking into
 * one is refused, so a click on it opens it on purpose (Open Door) instead.
 */
export function trappedDoor(d: Tiles & Pick<DungeonView, 'hazards'>, x: number, y: number): boolean {
  if (tileAt(d, x, y) !== '+') return false;
  const run = doorRun(d, x, y);
  if (!run) return false;
  const leaves = doorSquares(d.width, x, y, run);
  return (d.hazards ?? []).some((h) => h.live && h.area.some((i) => leaves.includes(i)));
}
